import React from 'react';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Box, SxProps, Theme } from '@mui/material';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { Cancel } from '@mui/icons-material';

export interface IAvatarSelection {
    children: JSX.Element;
    open: boolean;
    handleClose: () => void;
    paperStyle?: React.CSSProperties;
    childrenStyle?: SxProps<Theme>;
    paperSx?: SxProps<Theme>;
    disableClose?: boolean;
    /** Merged into the root `Dialog` `sx` (e.g. backdrop blur). */
    dialogSx?: SxProps<Theme>;
    /** Brand dashboard: dark surfaces and gold accents on paper / close control. */
    variant?: 'default' | 'brand';
}

const GenericModal: React.FC<IAvatarSelection> = ({
    children,
    open,
    handleClose,
    paperStyle,
    childrenStyle,
    paperSx,
    disableClose,
    dialogSx,
    variant = 'default',
}) => {
    const { themeStyle, device } = useThemeContext();
    const isBrand = variant === 'brand';

    return (
        <Box>
            <Dialog
                open={open}
                onClose={handleClose}
                color="blue"
                sx={
                    isBrand
                        ? {
                              '& .MuiBackdrop-root': { bgcolor: 'rgba(0,0,0,0.72)' },
                              ...dialogSx,
                          }
                        : dialogSx
                }
                PaperProps={{
                    style: isBrand
                        ? {
                              backgroundColor: '#262626',
                              height: 'auto',
                              maxHeight: '90vh',
                              width: device.isDesktop ? 520 : '92%',
                              maxWidth: 520,
                              overflowY: 'auto',
                              border: '1px solid rgba(72,72,71,0.45)',
                              borderRadius: 12,
                              ...paperStyle,
                          }
                        : {
                              backgroundColor: theme[themeStyle].pure_black,
                              height: '60%',
                              width: device.isDesktop ? '30%' : '80%',
                              overflowY: 'scroll',
                              border: '1px solid grey',
                              borderRadius: 10,
                              ...paperStyle,
                          },
                    sx: paperSx,
                }}>
                <DialogContent
                    sx={
                        isBrand
                            ? { p: 3, pb: 3, ...(childrenStyle as object) }
                            : childrenStyle
                    }>
                    {children}
                </DialogContent>
                {!disableClose && (
                    <DialogActions sx={{ position: 'absolute', right: 8, top: 8, p: 0, m: 0 }}>
                        <IconButton
                            color="primary"
                            aria-label="Close"
                            onClick={handleClose}
                            sx={
                                isBrand
                                    ? {
                                          borderRadius: '50%',
                                          border: '1px solid rgba(255,255,255,0.18)',
                                          width: 36,
                                          height: 36,
                                          color: '#ffffff',
                                          '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                                      }
                                    : undefined
                            }
                            style={
                                isBrand
                                    ? undefined
                                    : {
                                          borderRadius: '50%',
                                          borderWidth: 1,
                                          borderColor: theme[themeStyle].lightWhite,
                                          width: 30,
                                      }
                            }>
                            <Cancel sx={{ color: isBrand ? '#ffffff' : theme[themeStyle].lightWhite }} />
                        </IconButton>
                    </DialogActions>
                )}
            </Dialog>
        </Box>
    );
};

export { GenericModal };
